/**
 * Query and mutation hooks for the Customers screens.
 *
 * Query keys follow DESIGN.md section 6: ['customers', filtersHash, cursor] and
 * ['customer', customerKey]. Nothing here fetches directly; everything goes through
 * @/lib/api, which owns the transport, the idempotency key and the step-up retry.
 *
 * The reveal mutation is the one worth reading twice. It is Tier 2, so the API answers 403
 * step_up_required and the shared client opens A2's step-up dialog and retries. The hook
 * does not implement step-up itself, and it must not: one implementation, one place.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  CommunicationsPage,
  CustomerOrdersPage,
  CustomerProfile,
  CustomerRow,
  MessageTemplate,
  Page,
  RewardsPage,
} from './types';
import type { CustomerNote } from '@elutide/shared';

export interface CustomerFilters {
  q?: string;
  segment?: string[];
  tag?: string[];
  has_balance?: boolean;
  sort?: string;
}

export function filtersHash(filters: CustomerFilters): string {
  return JSON.stringify({
    q: filters.q ?? '',
    segment: [...(filters.segment ?? [])].sort(),
    tag: [...(filters.tag ?? [])].sort(),
    has_balance: filters.has_balance ?? null,
    sort: filters.sort ?? '',
  });
}

export function useCustomers(
  filters: CustomerFilters,
  cursor: string | null,
): UseQueryResult<Page<CustomerRow>> {
  return useQuery({
    queryKey: ['customers', filtersHash(filters), cursor],
    queryFn: () =>
      api.get<Page<CustomerRow>>('/customers', {
        query: {
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.segment?.length ? { segment: filters.segment } : {}),
          ...(filters.tag?.length ? { tag: filters.tag } : {}),
          ...(filters.has_balance === undefined ? {} : { has_balance: filters.has_balance }),
          ...(filters.sort ? { sort: filters.sort } : {}),
          ...(cursor ? { cursor } : {}),
        },
      }),
    staleTime: 60_000,
  });
}

export function useCustomer(customerKey: string): UseQueryResult<CustomerProfile> {
  return useQuery({
    queryKey: ['customer', customerKey],
    queryFn: () => api.get<CustomerProfile>(`/customers/${encodeURIComponent(customerKey)}`),
    staleTime: 30_000,
  });
}

export function useCustomerOrders(customerKey: string): UseQueryResult<CustomerOrdersPage> {
  return useQuery({
    queryKey: ['customer', customerKey, 'orders'],
    queryFn: () => api.get<CustomerOrdersPage>(`/customers/${encodeURIComponent(customerKey)}/orders`),
  });
}

export function useCustomerRewards(customerKey: string, enabled: boolean): UseQueryResult<RewardsPage> {
  return useQuery({
    queryKey: ['customer', customerKey, 'rewards'],
    queryFn: () => api.get<RewardsPage>(`/customers/${encodeURIComponent(customerKey)}/rewards`),
    enabled,
  });
}

export function useCustomerCommunications(
  customerKey: string,
  enabled: boolean,
): UseQueryResult<CommunicationsPage> {
  return useQuery({
    queryKey: ['customer', customerKey, 'communications'],
    queryFn: () => api.get<CommunicationsPage>(`/customers/${encodeURIComponent(customerKey)}/communications`),
    enabled,
  });
}

export function useMessageTemplates(): UseQueryResult<Page<MessageTemplate>> {
  return useQuery({
    queryKey: ['customers', 'templates'],
    queryFn: () => api.get<Page<MessageTemplate>>('/customers/templates'),
    staleTime: 15 * 60_000,
  });
}

export interface RevealResult {
  value: string | null;
  reveals_remaining: number;
}

export function useRevealContact(
  customerKey: string,
): UseMutationResult<RevealResult, unknown, { field: 'email' | 'phone'; reason: string }> {
  return useMutation({
    mutationFn: (input) =>
      api.post<RevealResult>(`/customers/${encodeURIComponent(customerKey)}/reveal`, input),
  });
}

export function useAddNote(customerKey: string): UseMutationResult<CustomerNote, unknown, { body: string; pinned: boolean }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.post<CustomerNote>(`/customers/${encodeURIComponent(customerKey)}/notes`, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['customer', customerKey] });
    },
  });
}

export function useAddTag(customerKey: string): UseMutationResult<{ tags: string[] }, unknown, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (tag) => api.post<{ tags: string[] }>(`/customers/${encodeURIComponent(customerKey)}/tags`, { tag }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['customer', customerKey] });
    },
  });
}

export function useRemoveTag(customerKey: string): UseMutationResult<{ tags: string[] }, unknown, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (tag) =>
      api.del<{ tags: string[] }>(`/customers/${encodeURIComponent(customerKey)}/tags/${encodeURIComponent(tag)}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['customer', customerKey] });
    },
  });
}

export interface SendMessageInput {
  channel: 'email' | 'sms';
  template: string;
  variables: Record<string, string>;
  note: string | null;
  reason: string;
  hold_seconds: number;
}

/**
 * Not optimistic. DESIGN.md section 6 is explicit that anything which leaves the building
 * shows pending, waits for the server and renders the server result.
 */
export function useSendMessage(customerKey: string): UseMutationResult<unknown, unknown, SendMessageInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.post(`/customers/${encodeURIComponent(customerKey)}/message`, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['customer', customerKey, 'communications'] });
    },
  });
}

export function useIssueCredit(
  customerKey: string,
): UseMutationResult<unknown, unknown, { delta_cents: number; reason: string; note: string | null; typed_amount?: string }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.post(`/customers/${encodeURIComponent(customerKey)}/credit`, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['customer', customerKey] });
    },
  });
}

export function useRequestCredit(
  customerKey: string,
): UseMutationResult<unknown, unknown, { delta_cents: number; reason: string }> {
  return useMutation({
    mutationFn: (input) => api.post(`/customers/${encodeURIComponent(customerKey)}/credit/request`, input),
  });
}
